import React                                    from 'react';
import { Outlet, useLocation, useNavigate }     from 'react-router-dom';
import {Box, useMediaQuery}                     from "@mui/material"
import Navbar                                   from "../Navbar"
import NavMenuButton                            from "../NavMenuButton"
import BrandMark                                from "../BrandMark"
import TutorStylePicker                         from "../app/TutorStylePicker"

/*
 * Study mode: the question, topic and review screens give the user the whole window — no
 * Navbar, no offset for it — and close back to the prepkit dashboard via the X. Matches
 * /prepkit/{id}/q/{code}, /prepkit/{id}/t/{topicId}[/q/{code}] and /prepkit/{id}/report.
 * The dashboard itself (/prepkit/{id}) is not study mode.
 */
const STUDY_PATH = /^\/prepkit\/([^/]+)\/(q\/|t\/|report\/?$)/

const studyModeFor = (pathname) => {
	const match = STUDY_PATH.exec(pathname || '')
	return match ? {prepKitId: match[1]} : null
}

/**
 * The single app layout: Navbar + routed content.
 *
 * By default the content area is a centred flex column offset below the fixed Navbar --
 * what the customer-facing screens expect. Pass `plain` for a bare full-width wrapper
 * (used by the admin section, which manages its own layout and must not be centred).
 */
const AppLayout = ({ toggleTheme, plain = false }) => {
	const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('md'))
	const navigate = useNavigate()
	const {pathname} = useLocation()
	const study = studyModeFor(pathname)

	if (plain) {
		return (
			<div>
				<Navbar toggleTheme={toggleTheme} />
				<div>
					<Outlet />
				</div>
			</div>
		)
	}

	return (
		<Box display={"flex"} flex={1}>
			{ !study && <Navbar toggleTheme={toggleTheme} /> }
			{
				/*
				 * Study mode has no Navbar, so it gets the same brand mark and the same hamburger
				 * menu in the same places (080). Geometry mirrors the Navbar's Toolbar — 56/64px
				 * band, 16/24px horizontal padding, logo at width 60 with its 5px nudge — so the
				 * controls land where they do on every other screen.
				 *
				 * The row is full width but pointer-events:none, with auto restored on the two
				 * controls: the study screens use the top band (topic back-link, Ask Birdsy) and
				 * it must stay clickable between the corners.
				 *
				 * zIndex 1400 keeps it above the full-screen topic title / score overlays (1100).
				 */
				study &&
				<Box sx={{
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					zIndex: 1400,
					height: {xs: 56, sm: 64},
					px: {xs: 2, sm: 3},
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					pointerEvents: 'none',
				}}>
					{/* below md the content runs to the left edge — no gutter for the mark */}
					<BrandMark
						onClick={() => navigate('/')}
						sx={{
							pointerEvents: 'auto',
							display: {xs: 'none', md: 'flex'},
						}}
					/>
					{/*
					  * The theme control is not lost — it lives inside this menu, exactly as it
					  * does on the dashboard. No close button: the way out is the Dashboard
					  * control in the screen's own navigation.
					  */}
					<Box sx={{pointerEvents: 'auto', ml: 'auto', display: 'flex', alignItems: 'center'}}>
						{/* the same control in the same place as on every other screen (170) */}
						<TutorStylePicker compact/>
						<NavMenuButton toggleTheme={toggleTheme}/>
					</Box>
				</Box>
			}
			<Box
				mt={study ? 0 : `${isSmallScreen ? 58 : 72}px`}
				display={"flex"}
				flex={1}
				minWidth={0}  /* let content wrap instead of growing this column to fit it */
				flexDirection={"column"}
				justifyContent={"flex-start"}
				/*
				 * Centring makes children shrink-to-fit, which stops a study screen's full-bleed
				 * panes from reaching the window edges (the routed content sits inside wrappers
				 * that would size to their content). Study mode stretches instead.
				 */
				alignItems={study ? "stretch" : "center"}
			>
				<Outlet /> {/* This will render the matched child route */}
			</Box>
		</Box>
	);
};

export default AppLayout;
/*
 * Exported for the trial banner (169), which has to know whether the Navbar exists on this route --
 * in study mode it does not, and the banner sits in a band this file draws instead. Sharing the
 * matcher keeps a second copy of STUDY_PATH from drifting out of step with this one.
 */
export {studyModeFor};
